import {
	createContext,
	type PropsWithChildren,
	useEffect,
	useState,
} from "react";

import { useLocation, useNavigate } from "react-router-dom";

import { usePartnerUserStore } from "@libs/acp/store/partnerUser/usePartnerUserStore";

import { checkSessionValidity } from "@libs/acp/utils/handleToken";

import useContextWrapper from "./hook";
import { useTracking } from "@libs/acp/hooks/useTracking";

import type { PartnerAuthenticationContextType } from "./types";

export const PartnerAuthenticationContext =
	createContext<PartnerAuthenticationContextType>({
		isLoading: true,
		isAuthenticated: false,
		document: "",
		tokens: {
			accessToken: "",
		},
	});

export const usePartnerAuthentication = () =>
	useContextWrapper(PartnerAuthenticationContext, {
		name: "PartnerAuthentication",
		provider: "PartnerAuthenticationProvider",
	});

export const PartnerAuthenticationProvider = ({
	children,
}: PropsWithChildren) => {
	// State
	const [isLoading, setIsLoading] = useState(true);

	// Store
	const { authenticatedUser, resetStore } = usePartnerUserStore();

	const navigate = useNavigate();
	const location = useLocation();

	const { access_token } = authenticatedUser.partnerUser.auth || {};

	// Hooks
	const { userAuthenticatedEvent } = useTracking("sign-in");

	const checkSession = async () => {
		try {
			setIsLoading(true);

			//#region Validate session
			// Get the access and refresh tokens from the user store
			const { access_token } =
				usePartnerUserStore.getState().authenticatedUser.partnerUser.auth;

			const { customer } =
				usePartnerUserStore.getState().authenticatedUser.partnerUser;

			// Check if the session is valid
			const hasValidSession = checkSessionValidity(access_token);
			if (hasValidSession) {
				navigate(`/home-partner${location.search}`, { replace: true });

				userAuthenticatedEvent({
					authenticationType: "cached-sign-in",
					customerId: customer.document,
					isUnified: true,
				});

				return;
			}
		} catch (error) {
			console.error("Error during partner authentication check:", error);

			resetStore();
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		checkSession();
	}, [isLoading]);

	return (
		<PartnerAuthenticationContext.Provider
			value={{
				isLoading,
				isAuthenticated: !isLoading && Boolean(access_token),
				tokens: {
					accessToken: access_token,
				},
				document:
					usePartnerUserStore.getState().authenticatedUser.partnerUser.customer
						.document,
			}}
		>
			{children}
		</PartnerAuthenticationContext.Provider>
	);
};
